import { useCallback, useEffect, useRef, useState } from "react";

import { StorageKey, readStored, writeStored } from "../lib/storage";

export const SIDEBAR_MIN_WIDTH = 180;
export const SIDEBAR_MAX_WIDTH = 480;
export const SIDEBAR_DEFAULT_WIDTH = 260;

function clampWidth(value: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(value)));
}

function initialWidth(): number {
  const stored = Number(readStored(StorageKey.sidebarWidth));
  return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : SIDEBAR_DEFAULT_WIDTH;
}

export interface ResizableSidebar {
  width: number;
  collapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (value: boolean) => void;
  /** Bind to the drag handle sitting on the sidebar's trailing edge. */
  startResize: (clientX: number) => void;
  nudge: (delta: number) => void;
  resizing: boolean;
}

/**
 * Sidebar width and collapsed state, both persisted per browser (note 03 §4).
 * Dragging updates width live; the value is written to storage on release so a
 * drag is one storage write rather than one per pointer move.
 */
export function useResizableSidebar(): ResizableSidebar {
  const [width, setWidth] = useState<number>(initialWidth);
  const [collapsed, setCollapsedState] = useState<boolean>(
    () => readStored(StorageKey.sidebarCollapsed) === "true",
  );
  const [resizing, setResizing] = useState(false);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    writeStored(StorageKey.sidebarCollapsed, String(value));
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((value) => {
      writeStored(StorageKey.sidebarCollapsed, String(!value));
      return !value;
    });
  }, []);

  const nudge = useCallback((delta: number) => {
    setWidth((current) => {
      const next = clampWidth(current + delta);
      writeStored(StorageKey.sidebarWidth, String(next));
      return next;
    });
  }, []);

  const startResize = useCallback((clientX: number) => {
    const originX = clientX;
    const originWidth = widthRef.current;
    let latest = originWidth;
    setResizing(true);

    const onMove = (event: MouseEvent): void => {
      latest = clampWidth(originWidth + (event.clientX - originX));
      setWidth(latest);
    };
    const onUp = (): void => {
      setResizing(false);
      writeStored(StorageKey.sidebarWidth, String(latest));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  useEffect(() => {
    if (!resizing) return;
    const previous = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = previous;
    };
  }, [resizing]);

  return { width, collapsed, toggleCollapsed, setCollapsed, startResize, nudge, resizing };
}
